import { memo, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useParams } from "react-router";

import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import useIacManagementStore from "views/Account/IacManagement/useIacManagementStore";
import { IacManagementGroupBy } from "views/Account/IacManagement/types";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";

import IacManagementTableBodyRow from ".";
import { RenderResourceRowContent } from "../types";
import useIacTableStore from "../useIacTableStore";
import { selectTableRowByIndex } from "../useIacTableStore/tableDataSelectors";

type IacManagementTableRowResourceProps = {
  rowIndex: number;
  measureRef: (node: HTMLDivElement) => void;
  renderContent: RenderResourceRowContent;
};

const IacManagementTableRowResource = ({
  rowIndex,
  measureRef,
  renderContent,
}: IacManagementTableRowResourceProps) => {
  const { resourceId } = useParams<{ resourceId?: string }>();

  const selectorResult = useIacTableStore(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      row: selectTableRowByIndex(state, rowIndex),
    }))
  );

  const groupBy = useIacManagementStore((state) => state.groupBy);
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const { columns, row, isActive } = useMemo(() => {
    const isActive = resourceId === selectorResult.row?.id;

    return {
      columns: selectorResult.columns,
      row: selectorResult.row,
      isActive,
    };
  }, [resourceId, selectorResult.columns, selectorResult.row]);

  if (!row || !("thirdPartyMetadata" in row)) {
    return null;
  }

  return (
    <IacManagementTableBodyRow
      {...keyboardProps}
      forwardRef={measureRef}
      dataIndex={rowIndex}
      isGroupChild={groupBy !== IacManagementGroupBy.None}
      isActive={isActive}
    >
      {renderContent({
        columns,
        row,
      })}
    </IacManagementTableBodyRow>
  );
};

export default memo(IacManagementTableRowResource);
